import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../context/StoreContext';
import reportsService from '../services/reportsService';
import supplierService from '../services/supplierService';
import { toDisplayDateValue } from '../utils/dateUtils';

const defaultSummary = {
  totalSkus: 0,
  totalUnits: 0,
  inventoryValue: 0,
  retailValue: 0,
};

const currency = (value) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const InventorySnapshotsPage = () => {
  const { activeStore } = useStore();
  const [filters, setFilters] = useState({
    date: '',
    supplierId: '',
    page: 1,
    limit: 200,
  });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(defaultSummary);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 200 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [snapshotDate, setSnapshotDate] = useState('');

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const response = await supplierService.fetchSuppliers({ limit: 300, compact: true });
        setSuppliers(response?.data || response || []);
      } catch (err) {
        console.warn('Unable to load suppliers for inventory snapshots', err);
      }
    };
    loadSuppliers();
  }, []);

  useEffect(() => {
    const fetchSnapshots = async () => {
      if (!activeStore) return;
      setLoading(true);
      setError('');
      try {
        const params = {
          storeId: activeStore._id,
          page: filters.page,
          limit: filters.limit,
        };
        if (filters.date) params.date = filters.date;
        if (filters.supplierId) params.supplierId = filters.supplierId;
        const response = await reportsService.getInventorySnapshots(params);
        const payload = response.data || {};
        setRows(payload.data || []);
        setSummary(payload.summary || defaultSummary);
        setPagination(payload.pagination || { page: filters.page, pages: 1, total: 0, limit: filters.limit });
        setSnapshotDate(payload.snapshotDate || filters.date || '');
      } catch (err) {
        console.error('Failed to load inventory snapshots', err);
        setError(err.response?.data?.message || 'Failed to load inventory snapshots.');
        setRows([]);
        setSummary(defaultSummary);
        setPagination({ page: 1, pages: 1, total: 0, limit: filters.limit });
      } finally {
        setLoading(false);
      }
    };
    fetchSnapshots();
  }, [activeStore, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > (pagination.pages || 1)) return;
    setFilters((prev) => ({ ...prev, page: nextPage }));
  };

  const summaryCards = useMemo(
    () => [
      { label: 'Snapshot date', value: snapshotDate ? toDisplayDateValue(snapshotDate) : 'N/A' },
      { label: 'SKUs counted', value: summary.totalSkus?.toLocaleString() || '0' },
      { label: 'Units on hand', value: summary.totalUnits?.toLocaleString() || '0' },
      { label: 'Inventory value (cost)', value: currency(summary.inventoryValue) },
      { label: 'Retail value', value: currency(summary.retailValue) },
    ],
    [summary, snapshotDate]
  );

  if (!activeStore) {
    return <div className="p-6 text-slate-600">Please select a store to view inventory snapshots.</div>;
  }

  const exportColumns = [
    { key: 'sku', label: 'SKU' },
    { key: 'name', label: 'Product' },
    { key: 'supplierName', label: 'Supplier' },
    { key: 'quantityOnHand', label: 'Quantity' },
    { key: 'unitCost', label: 'Unit Cost' },
    { key: 'unitPrice', label: 'Unit Price' },
    { key: 'inventoryValue', label: 'Inventory Value' },
    { key: 'retailValue', label: 'Retail Value' },
  ];

  const buildExportRows = () =>
    rows.map((row) => ({
      sku: row.sku || '',
      name: row.name || '',
      supplierName: row.supplierName || '',
      quantityOnHand: row.quantityOnHand ?? row.quantity ?? 0,
      unitCost: Number(row.unitCost || 0).toFixed(2),
      unitPrice: Number(row.unitPrice || 0).toFixed(2),
      inventoryValue: Number(row.inventoryValue || 0).toFixed(2),
      retailValue: Number(row.retailValue || 0).toFixed(2),
    }));

  const downloadCsv = () => {
    const rowsForExport = buildExportRows();
    const csv = [
      exportColumns.map((c) => c.label).join(','),
      ...rowsForExport.map((row) =>
        exportColumns
          .map((col) => {
            const value = row[col.key];
            const escaped = String(value ?? '').replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory-snapshot-${snapshotDate || 'latest'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Snapshot Date</label>
          <input
            type="date"
            value={filters.date}
            onChange={(e) => handleFilterChange('date', e.target.value)}
            className="w-full border rounded-md p-2 focus:ring focus:ring-blue-200"
          />
          <p className="text-xs text-slate-500 mt-1">Leave blank to load the most recent snapshot.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Supplier</label>
          <select
            value={filters.supplierId}
            onChange={(e) => handleFilterChange('supplierId', e.target.value)}
            className="w-full border rounded-md p-2 focus:ring focus:ring-blue-200"
          >
            <option value="">All suppliers</option>
            {suppliers.map((supplier) => (
              <option key={supplier._id} value={supplier._id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end space-x-2">
          <button
            onClick={downloadCsv}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            disabled={rows.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-white shadow rounded-md p-4">
            <p className="text-xs uppercase text-slate-500">{card.label}</p>
            <p className="text-xl font-semibold mt-1">{card.value}</p>
          </div>
        ))}
      </div>

      {error && <div className="bg-red-100 text-red-700 border border-red-200 rounded p-3">{error}</div>}

      <div className="bg-white shadow rounded-md overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-semibold text-slate-600">SKU</th>
              <th className="px-4 py-2 text-left font-semibold text-slate-600">Product</th>
              <th className="px-4 py-2 text-left font-semibold text-slate-600">Supplier</th>
              <th className="px-4 py-2 text-right font-semibold text-slate-600">Quantity</th>
              <th className="px-4 py-2 text-right font-semibold text-slate-600">Unit Cost</th>
              <th className="px-4 py-2 text-right font-semibold text-slate-600">Unit Price</th>
              <th className="px-4 py-2 text-right font-semibold text-slate-600">Inventory Value</th>
              <th className="px-4 py-2 text-right font-semibold text-slate-600">Retail Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan="8" className="px-4 py-6 text-center text-slate-500">
                  Loading snapshot…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="8" className="px-4 py-6 text-center text-slate-500">
                  No snapshot rows available for this date.
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => (
                <tr key={`${row.product}-${row.snapshotDate}`}>
                  <td className="px-4 py-2 text-slate-700">{row.sku || '-'}</td>
                  <td className="px-4 py-2 text-slate-700">{row.name}</td>
                  <td className="px-4 py-2 text-slate-600">{row.supplierName || '—'}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{row.quantityOnHand ?? row.quantity ?? 0}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{currency(row.unitCost)}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{currency(row.unitPrice)}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{currency(row.inventoryValue)}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{currency(row.retailValue)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pagination.pages || 1} (Total {pagination.total || 0} rows)
        </div>
        <div className="space-x-2">
          <button
            onClick={() => handlePageChange((pagination.page || 1) - 1)}
            disabled={(pagination.page || 1) <= 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange((pagination.page || 1) + 1)}
            disabled={(pagination.page || 1) >= (pagination.pages || 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventorySnapshotsPage;
